import React from "react";
import { Container } from "reactstrap";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import {
  CompetitionHighlightsGrid,
  DogPage,
  DogFactsBar,
  DogProfileActions
} from "components/Dogs/DogComponents.js";
import { LitterListingGrid } from "components/Litters/LitterComponents.js";
import SEO from "components/SEO/SEO.js";
import DogGallery from "./DogGallery";

const agilityIcon = require("../../assets/img/icon-agility.png");
const barnHuntIcon = require("../../assets/img/icon-barnhunt.png");
const discIcon = require("../../assets/img/icon-disc.png");
const dockDivingIcon = require("../../assets/img/icon-dockdiving.png");
const flyballIcon = require("../../assets/img/icon-flyball.png");
const stackImage = require("../../assets/img/dogs/nimona/stack3.jpg");
const pedigreeImage = require("../../assets/img/dogs/litters/vh2022/pedigree.jpg");
const venusProfileImage = require("../../assets/img/dogs/profile-venus.jpg");
const clearances = [
  {
    id: "nimona-genetic-summary",
    label: "Embark Health DNA",
    shortLabel: "DNA Summary",
    category: "Genetic",
    detailLabel: "Embark DNA",
    date: "August 2022",
    image: require("../../assets/img/dogs/nimona/clearance1.jpg"),
    alt: "Nimona breed-relevant DNA conditions summary"
  },
  {
    id: "nimona-dcm1-genetic-result",
    label: "DCM1 Information",
    shortLabel: "DCM1",
    category: "Genetic",
    detailLabel: "Dilated Cardiomyopathy",
    date: "August 2022",
    image: require("../../assets/img/dogs/nimona/clearance2.jpg"),
    alt: "Nimona DCM1 genetic result"
  },
  {
    id: "nimona-ofa-eyes-2022",
    label: "OFA Eye Certification",
    shortLabel: "Eyes 2022",
    category: "Eyes",
    detailLabel: "Eyes",
    date: "November 2022",
    image: require("../../assets/img/dogs/nimona/clearance3.jpg"),
    alt: "Nimona OFA eye certification from November 2022"
  },
  {
    id: "nimona-ofa-baer-2022",
    label: "OFA BAER Hearing Certification",
    shortLabel: "BAER",
    category: "Other",
    detailLabel: "Hearing",
    date: "November 2022",
    image: require("../../assets/img/dogs/nimona/clearance5.jpg"),
    alt: "Nimona OFA BAER hearing certification from November 2022"
  },
  {
    id: "nimona-pennhip-2024",
    label: "PennHIP Report",
    shortLabel: "PennHIP",
    category: "Orthopedic",
    detailLabel: "Hips",
    date: "February 2024",
    image: require("../../assets/img/dogs/nimona/clearance4.jpg"),
    alt: "Nimona PennHIP report from February 2024"
  },
  {
    id: "nimona-pennhip-laxity-profile-2024",
    label: "Breed Laxity Avg",
    shortLabel: "Laxity",
    category: "Orthopedic",
    detailLabel: "Hips",
    date: "February 2024",
    image: require("../../assets/img/dogs/nimona/clearance4a.jpg"),
    alt: "Nimona PennHIP distraction index laxity profile"
  },
  {
    id: "nimona-ofa-patellas-2024",
    label: "OFA Patella Cert",
    shortLabel: "Patellas",
    category: "Orthopedic",
    detailLabel: "Patellas",
    date: "February 2024",
    image: require("../../assets/img/dogs/nimona/clearance6.jpg"),
    alt: "Nimona OFA patella evaluation from February 2024"
  },
  {
    id: "nimona-ofa-hips-2024",
    label: "OFA Prelim Hip Cert",
    shortLabel: "OFA Hips",
    category: "Orthopedic",
    detailLabel: "Hips",
    date: "February 2024",
    image: require("../../assets/img/dogs/nimona/clearance7.jpg"),
    alt: "Nimona OFA hip evaluation from February 2024"
  },
  {
    id: "nimona-ofa-shoulders-2024",
    label: "OFA Shoulder Cert",
    shortLabel: "Shoulders",
    category: "Orthopedic",
    detailLabel: "Shoulders",
    date: "February 2024",
    image: require("../../assets/img/dogs/nimona/clearance8.jpg"),
    alt: "Nimona OFA shoulder evaluation from February 2024"
  },
  {
    id: "nimona-uc-davis-cddy-cdpa-2024",
    label: "UC Davis CDDY/CDPA",
    shortLabel: "DNA CDDY/CDPA",
    category: "Genetic",
    detailLabel: "CDDY/CDPA",
    date: "April 2024",
    image: require("../../assets/img/dogs/nimona/clearance9.jpg"),
    alt: "Nimona UC Davis CDDY and CDPA test report from April 2024"
  },
  {
    id: "nimona-ofa-eyes-2025",
    label: "OFA Eye Certification",
    shortLabel: "Eyes 2025",
    category: "Eyes",
    detailLabel: "Eyes",
    date: "January 2025",
    image: require("../../assets/img/dogs/nimona/clearance10.jpg"),
    alt: "Nimona OFA eye certification from January 2025"
  }
];

const pedigreeData = {
  image: pedigreeImage,
  alt: "Venus x Hop Sing pedigree",
  linkedDogs: [
    {
      id: "venus",
      name: "Venus",
      relationship: "Dam",
      breed: "Border Collie",
      image: venusProfileImage,
      profileHref: "/dogs/venus"
    }
  ]
};

const galleryImages = [
  require("../../assets/img/dogs/nimona/reg1.jpg"),
  require("../../assets/img/dogs/nimona/reg2.jpg"),
  require("../../assets/img/dogs/nimona/reg3.jpg"),
  require("../../assets/img/dogs/nimona/reg4.jpg"),
  require("../../assets/img/dogs/nimona/reg5.jpg"),
  require("../../assets/img/dogs/nimona/reg6.jpg"),
  require("../../assets/img/dogs/nimona/reg7.jpg"),
  require("../../assets/img/dogs/nimona/reg8.jpg"),
  require("../../assets/img/dogs/nimona/reg9.jpg"),
  require("../../assets/img/dogs/nimona/reg10.jpg"),
  require("../../assets/img/dogs/nimona/reg11.jpg"),
  require("../../assets/img/dogs/nimona/reg12.jpg"),
  require("../../assets/img/dogs/nimona/reg13.jpg"),
  require("../../assets/img/dogs/nimona/reg14.jpg"),
  require("../../assets/img/dogs/nimona/reg15.jpg"),
  require("../../assets/img/dogs/nimona/reg16.jpg"),
  require("../../assets/img/dogs/nimona/reg17.jpg"),
  require("../../assets/img/dogs/nimona/reg18.jpg"),
  require("../../assets/img/dogs/nimona/reg19.jpg"),
  require("../../assets/img/dogs/nimona/reg20.jpg"),
  require("../../assets/img/dogs/nimona/reg21.jpg"),
  require("../../assets/img/dogs/nimona/reg22.jpg"),
  require("../../assets/img/dogs/nimona/reg23.jpg"),
  require("../../assets/img/dogs/nimona/reg24.jpg")
];

const nimonaProfile = {
  name: "Nimona",
  registeredName: "Endgame's I'm a Shark FMCH DEX HDEA AE DDSE RATN",
  about: "Nimona is a black and white smooth coat border collie x jack russell mix from the Venus x HopSing litter. She was the pick of the litter and stayed with me to play dog sports. She had an unfortunate growth plate injury at 3 months which caused her radius and ulna on her right leg to grow at different rates. Her leg was operated on to prevent elbow damage, but the legs remain different lengths. She performs great despite this and had many big accomplishments at a young age. Shehad one litter with Jerome in 2025 and was spayed so she can focus on her sport career.",
  facts: [
    { label: "Breed", value: "Sport Mix" },
    { label: "Born", value: "07/16/22" },
    { label: "Height", value: '15.75"' },
    { label: "Weight", value: "21lbs" }
  ],
  temperament: "Nimona is very cuddly and affectionate in the house. She is the lap dog and claims her spot next to me every day. She loves other dogs and is very appropriate with her interactions with them. She is high drive, biddable, and high energy with a lovely off switch. She has some avoidance to being grabbed by strangers since her surgery, but loves visitors at home and approaches most strangers willingly outside the house. She works for other people in agility and will let strangers reward her with food or tugging during practice. Her drive outweighs her sensitivites most of the time and I consider her to be a high arousal dog.",
  athleticAbility: "Nim has surpassed all expectations despite her disability. She took to dock diving immediately with 21ft jumps her first competition and picked up flyball quickly, debuting at 17 months with only a two not clean heats. She is a reliable teammate and even more rarely makes mistakes in the flyball lanes these days. She played disc competitively for a friend and has good control over her body when catching discs. Her agility training was limited early due to her leg, but after doing well I opted to train her more. She has beautiful jumping form and extension, and lovely collection that wasn't natural for her. She is very much a go fast, go far dog every chance she gets. She also took to barn hunt immediately without formal training and I expect to return to it in a few years as her retirement sport.",
  video: {
    src: "https://www.youtube.com/embed/videoseries?si=dQx2cKE8PQmeVmY2&amp;list=PLMlK4YoX-9JE"
  }
};

const healthGroups = [
  {
    title: "Orthopedic & Screening",
    items: [
      { label: "Hips", value: "OFA Good*, Pennhip .41/.36" },
      { label: "Elbows", value: "Unchecked" },
      { label: "Shoulders", value: "OFA Normal" },
      { label: "Eyes", value: "Clear (2022, 2025)" },
      { label: "Knees", value: "OFA Normal" },
      { label: "Hearing", value: "BAER Normal" },
      { label: "Spine", value: "LTV free" }
    ],
    note: "* OFA Prelim"
  },
  {
    title: "DNA Panel",
    columns: [
      {
        title: "Border Collie",
        items: [
          { label: "CEA", value: "Clear" },
          { label: "DM", value: "Clear" },
          { label: "SN", value: "Clear" },
          { label: "TNS", value: "Clear" },
          { label: "IGS", value: "Clear" },
          { label: "NCL5", value: "Clear" },
          { label: "G/G", value: "Clear" },
          { label: "DH", value: "Clear" },
          { label: "EOD", value: "Clear" }
        ]
      },
      {
        title: "Jack Russell",
        items: [
          { label: "SCID", value: "Clear" },
          { label: "PLL", value: "Clear" },
          { label: "CDDY/IVDD", value: "Clear" },
          { label: "CMS", value: "Clear" },
          { label: "SCA", value: "Clear" },
          { label: "ENAM", value: "Clear" },
          { label: "HUU", value: "Clear" }
        ]
      },
      {
        title: "Other",
        items: [
          { label: "DCM1", value: "Carrier*" }
        ]
      },
      {
        title: "Breeds",
        items: [
          { label: "Border Collie", value: "50%" },
          { label: "Jack Russell", value: "50%" }
        ]
      },
    ],
    note: "* The DCM1 genetic test does not affect jack russells or border collies."
  }
];

const competitionHighlights = [
  {
    sport: "Dock Diving",
    desktopColumn: "left",
    order: 1,
    icon: dockDivingIcon,
    groups: [
      {
        label: "Personal Bests",
        items: ["Distance: 26ft 5in", "Air Retrieve: 17ft", "Hydrodash: 16.2"]
      },
      {
        label: "NADD",
        items: [
          "2025 NADD Diving Dogs winner",
          "2024 NADD 1st Lap Elite Air Retrieve",
          "2024 NADD 2nd Lap Elite Distance",
          "2024 Nationals 1st Lap Elite Hydrodash",
          "2024 Nationals 2nd Lap Elite Air Retrieve",
          "2024 Nationals 6th Lap Elite Distance"
        ]
      },
      {
        label: "Regional",
        items: [
          "2024 South East Regional 1st Lap Elite Distance",
          "2024 South East Regional 1st Lap Elite Air Retrieve",
          "2024 South East Regional 1st Lap Elite Hydrodash",
          "Dock Dogs #1 USA club lap dog 2023"
        ]
      }
    ]
  },
  {
    sport: "Disc",
    desktopColumn: "right",
    order: 1,
    icon: discIcon,
    groups: [
      {
        label: "Skyhoundz",
        items: [
          "2024 Skyhoundz Worlds Extreme Distance Micro Dogs 2nd",
          "2024 Skyhoundz Worlds Classic Sport Micro Dog 3rd",
          "Bullseye PB: 50",
          "Xtreme Distance Light PB: 191ft"
        ]
      },
      {
        label: "Updog",
        items: [
          "2026 Updif 4th overall 7Up",
          "2026 Updif 2nd Midi 7Up",
          "2026 Updif 2nd Midi Boom",
          "2026 Updif Top 4% Midi division",
          "Throw and Go PB: 57",
          "7up PB: 81"
        ]
      },
      {
        label: "Toss and Fetch League",
        items: [
          "T&F 90:5 Summer 2024 #1 out of 3,129",
          "T&F Champs 2024 #18/837 worldwide",
          "T&F Spring 2023 #109 out of 3,245",
          "T&F Champs 2023 #1 Expert regional, #57 worldwide"
        ]
      }
    ]
  },
  {
    sport: "Flyball",
    desktopColumn: "left",
    order: 2,
    icon: flyballIcon,
    groups: [
      {
        label: "Personal Bests",
        items: ["PB: 3.731", "Pack: 3.841", "Average: 3.8 in start"]
      },
      {
        label: "Major Result",
        items: ["2026 Westminster Reg. 1 Winner"]
      }
    ]
  },
  {
    sport: "Agility",
    desktopColumn: "right",
    order: 2,
    icon: agilityIcon,
    groups: [
      {
        label: "UKI",
        items: ['2026 Pawspack DASH Triathlon 16" - 1st']
      },
      {
        label: "AKC",
        items: [
          '2026 Spring AKC league Sophomore - 1st 16"',
          '2026 Spring AKC league PhD - 2nd 16"',
          '2026 Summer AKC league PhD - 1st 16"',
        ]
      }
    ]
  },
  {
    sport: "Barn Hunt",
    icon: barnHuntIcon,
    groups: [
      {
        label: "Title",
        items: ["Novice title"]
      }
    ]
  }
];

const nimonaLitters = [
  {
    title: "Nimona x Jerome (2025)",
    href: "/litters/nimona-jerome-2025",
    image: require("../../assets/img/dogs/litters/nj2025/announcement4.jpg"),
    status: "Sport Mix Litter"
  }
];

function DogSectionHeading({ eyebrow, title, children }) {
  return (
    <div className="dog-detail-section-heading">
      {eyebrow && <p>{eyebrow}</p>}
      <h2>{title}</h2>
      <span aria-hidden="true"></span>
      {children}
    </div>
  );
}

function NimonaPage() {
  const hasVideo = Boolean(nimonaProfile.video && nimonaProfile.video.src);
  return (
    <>
      <SEO
        title="Nimona | Sport Mix Dog"
        description="Meet Nimona, a health-tested border collie x jack russell sport mix in Raleigh NC competing in dock diving, flyball, disc, agility, and barn hunt and dam of the 2025 Nimona x Jerome litter."
        path="/dogs/nimona"
      />
      <DogPage
        headerBg="nimona"
        pageClassName="dog-detail-page dog-nimona-page"
      >
        <section className="dog-facts-wrap" aria-label="Nimona at a glance">
          <Container>
            <DogFactsBar facts={nimonaProfile.facts} />
          </Container>
        </section>

        <section className="dog-detail-section dog-about-section">
          <Container>
            <div className="dog-about-layout">
              <div className="dog-about-copy">
                <div className="dog-back-link">
                  <Link to="/dogs">
                    <ArrowLeft aria-hidden="true" size={14} strokeWidth={2.2} /> Back to Dogs
                  </Link>
                </div>
                <DogSectionHeading eyebrow={`${nimonaProfile.registeredName}`} title={`About ${nimonaProfile.name}`} />
                <p>{nimonaProfile.about}</p>
              </div>
              <figure className="dog-stack-figure">
                <img alt="Nimona stacked in profile" src={stackImage} />
                <figcaption>{nimonaProfile.stats}</figcaption>
              </figure>
            </div>

            <div className="dog-health-spec-panel site-bg-light-blue">
              <div className="dog-health-panel-heading">
                <h2>Health & Genetics</h2>
                <span aria-hidden="true"></span>
              </div>
              <div className="dog-health-results">
                {healthGroups.map(group => (
                  <section
                    className={`dog-health-spec-group${group.title === "DNA Panel" ? " dog-health-dna-group" : ""}`}
                    key={group.title}
                  >
                    <h3>{group.title}</h3>
                    {group.columns ? (
                      <div className="dog-health-subgroup-columns">
                        {group.columns.map(column => (
                          <div className="dog-health-subgroup" key={column.title}>
                            <h4>{column.title}</h4>
                            <dl>
                              {column.items.map(item => (
                                <div className="dog-health-result" key={`${column.title}-${item.label}`}>
                                  <span className="dog-health-check" aria-hidden="true">✓</span>
                                  <dt>{item.label}</dt>
                                  <dd>{item.value}</dd>
                                </div>
                              ))}
                            </dl>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <dl>
                        {group.items.map(item => (
                          <div className="dog-health-result" key={`${group.title}-${item.label}`}>
                            <span className="dog-health-check" aria-hidden="true">✓</span>
                            <dt>{item.label}</dt>
                            <dd>{item.value}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                    {group.note && <p>{group.note}</p>}
                  </section>
                ))}
              </div>
              <section className="dog-health-spec-group dog-health-actions-group">
                <h3>Documents</h3>
                <DogProfileActions
                  dogName={nimonaProfile.name}
                  pedigreeData={pedigreeData}
                  clearances={clearances}
                  className="dog-profile-actions"
                />
              </section>
            </div>

            <div className="dog-traits-layout">
              <article>
                <h2>Temperament</h2>
                <span aria-hidden="true"></span>
                <p>{nimonaProfile.temperament}</p>
              </article>
              <article>
                <h2>Athletic Ability</h2>
                <span aria-hidden="true"></span>
                <p>{nimonaProfile.athleticAbility}</p>
              </article>
            </div>
          </Container>
        </section>

        <div className="dog-performance-wrap">
          {hasVideo && (
            <section className="dog-detail-section dog-video-section">
              <Container>
                <DogSectionHeading eyebrow={`Nimona in Action`} title="Sport highlights" />
                <div className="dog-video-feature">
                  <div className="dog-video-embed">
                    <div className="video-frame">
                      <iframe
                        src={nimonaProfile.video.src}
                        title={nimonaProfile.video.title}
                        loading="lazy"
                        frameBorder="0"
                        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      ></iframe>
                    </div>
                  </div>
                  <div className="dog-video-caption">
                    <p>Nimona excelled across a variety of sports beginning early in her life. She has earned national and regional wins in dock diving, competed successfully at Skyhoundz Worlds, and developed into a fast, reliable flyball dog with many 3.7 times. More recently, she has added agility wins and I'm looking forward to where this journey takes her.</p>
                  </div>
                </div>
              </Container>
            </section>
          )}
          <section className="dog-detail-section dog-competition-section">
            <Container>
              <div className="dog-health-panel-heading dog-competition-panel-heading">
                <h2>Competition Highlights</h2>
                <span aria-hidden="true"></span>
              </div>
              <CompetitionHighlightsGrid highlights={competitionHighlights} />
            </Container>
          </section>
        </div>

        <section className="dog-detail-section dog-litters-section">
          <Container>
            <DogSectionHeading title="Past Litters" />
            <LitterListingGrid
              rowClassName="litter-gallery-grid dog-detail-litter-grid"
              cardClassName="litter-nameplate-card litter-gallery-card"
              litters={nimonaLitters}
            />
          </Container>
        </section>

        <section className="dog-detail-section dog-gallery-section site-bg-dark-navy">
          <Container>
            <DogSectionHeading title="Photo Gallery" />
          </Container>
          <DogGallery images={galleryImages} />
        </section>
      </DogPage>
    </>
  );
}

export default NimonaPage;
